/**
 * ViewState <-> URL hash.
 *
 *   #y=2026&r=0.52&s=1.2&cx=640&cy=360
 *
 * `y` is the view year; `cx`/`cy` are the camera translation. (The SPEC sketch
 * used `x`/`y` for the translation, which collides with the year — `x` is still
 * accepted on read as an alias for `cx`.)
 */
import type { ViewState } from '@circle/shared';

export const HASH_KEYS = { year: 'y', rotation: 'r', scale: 's', tx: 'cx', ty: 'cy' } as const;

function round(value: number, places: number): number {
  const f = 10 ** places;
  return Math.round(value * f) / f;
}

export function encodeViewState(view: ViewState): string {
  const params = new URLSearchParams();
  params.set(HASH_KEYS.year, String(view.year));
  params.set(HASH_KEYS.rotation, String(round(view.rotation, 4)));
  params.set(HASH_KEYS.scale, String(round(view.scale, 5)));
  params.set(HASH_KEYS.tx, String(round(view.tx, 1)));
  params.set(HASH_KEYS.ty, String(round(view.ty, 1)));
  return params.toString();
}

function num(params: URLSearchParams, key: string, fallback: number): number {
  const raw = params.get(key);
  if (raw === null) return fallback;
  const value = Number(raw);
  return Number.isFinite(value) ? value : fallback;
}

/** Parse a hash (with or without a leading '#'), falling back per field. */
export function parseViewState(hash: string, fallback: ViewState): ViewState {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const year = Math.trunc(num(params, HASH_KEYS.year, fallback.year));
  const scale = num(params, HASH_KEYS.scale, fallback.scale);
  return {
    year: year >= 1 && year <= 9999 ? year : fallback.year,
    rotation: num(params, HASH_KEYS.rotation, fallback.rotation),
    scale: scale > 0 ? scale : fallback.scale,
    tx: num(params, HASH_KEYS.tx, num(params, 'x', fallback.tx)),
    ty: num(params, HASH_KEYS.ty, fallback.ty),
  };
}

export interface ViewStateUrlOptions {
  /** Debounce for hash writes, ms. */
  delay?: number;
  /** Called when the hash changes from outside (back/forward, pasted link). */
  onExternalChange?: (view: ViewState) => void;
}

/** Debounced two-way binding between the URL hash and a ViewState. */
export class ViewStateUrl {
  private timer: ReturnType<typeof setTimeout> | undefined;
  private pending: ViewState | undefined;
  private lastWritten = '';
  private readonly delay: number;
  private readonly listener = () => {
    if (location.hash.replace(/^#/, '') === this.lastWritten) return;
    this.options.onExternalChange?.(parseViewState(location.hash, this.fallback));
  };

  constructor(
    private readonly fallback: ViewState,
    private readonly options: ViewStateUrlOptions = {},
  ) {
    this.delay = options.delay ?? 250;
    if (options.onExternalChange) window.addEventListener('hashchange', this.listener);
  }

  /** ViewState from the current URL, falling back to the constructor default. */
  read(): ViewState {
    return parseViewState(location.hash, this.fallback);
  }

  write(view: ViewState): void {
    this.pending = view;
    if (this.timer !== undefined) return;
    this.timer = setTimeout(() => {
      this.timer = undefined;
      if (!this.pending) return;
      this.flush();
    }, this.delay);
  }

  flush(): void {
    if (!this.pending) return;
    const encoded = encodeViewState(this.pending);
    this.pending = undefined;
    if (encoded === this.lastWritten) return;
    this.lastWritten = encoded;
    history.replaceState(null, '', `#${encoded}`);
  }

  /**
   * Stop listening, and write whatever was still waiting for the debounce.
   * Leaving the page inside the 250 ms window would otherwise drop the last
   * camera, and the Back button would return to the one before it.
   */
  dispose(): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
    this.flush();
    window.removeEventListener('hashchange', this.listener);
  }
}
